import { Grid, Heading } from "@planningcenter/doxy-web"
import { Link } from "react-router-dom"
import { ChurchCenterCustomFieldApiRecordWithRelationships } from "source/myChurchCenter/Profile/types"
import { useSearchParams } from "../hooks/useSearchParams"
import { groupCustomFieldsByHeaders } from "./groupCustomFieldsByHeader"

const NO_CUSTOM_FIELD_DATA_VALUE = "—"

const singularFieldDatum = (
  record: ChurchCenterCustomFieldApiRecordWithRelationships,
) => record.relationships.field_data?.data[0]

export function CustomFields({
  records,
  canEdit,
}: {
  records: ChurchCenterCustomFieldApiRecordWithRelationships[]
  canEdit: boolean
}) {
  const [searchParams] = useSearchParams()
  const selectedProfileId = searchParams.get("profile")
  const currentHouseholdId = searchParams.get("household")
  const editLink = `edit?profile=${selectedProfileId}${currentHouseholdId ? `&household=${currentHouseholdId}` : ""}&section=custom-fields`
  const groupedRecords = groupCustomFieldsByHeaders(records)

  return groupedRecords.map((group, groupIndex) => (
    <div key={groupIndex}>
      <div className="d-f ai-c jc-sb mb-2">
        <div className="d-f fd-c">
          <Heading level={2} size={3} text={group.title} />
          {group?.description && (
            <p className="c-tint2 fs-4 mb-0">{group.description}</p>
          )}
        </div>
        {canEdit && (
          <Link
            to={`${editLink}&group=${groupIndex}`}
            className="minor-btn secondary-btn btn"
            style={{ zIndex: 0 }}
          >
            Edit
          </Link>
        )}
      </div>
      <dl className="action-drawer mb-4">
        <Grid columns={[1, { xs: 2 }]} gap={2}>
          {group.items.map((record, index) => (
            <Item key={index} record={record} />
          ))}
        </Grid>
      </dl>
    </div>
  ))
}

function Item({
  record,
}: {
  record: ChurchCenterCustomFieldApiRecordWithRelationships
}) {
  return (
    <div className="custom-field-item">
      <dt className="c-tint2 fs-4">
        {record.relationships.field_definition.data.attributes.name}
      </dt>
      <dd
        className="mb-1"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
      >
        <FieldValue record={record} />
      </dd>
    </div>
  )
}

function FieldValue({
  record,
}: {
  record: ChurchCenterCustomFieldApiRecordWithRelationships
}) {
  const dataType =
    record.relationships.field_definition?.data?.attributes?.data_type
  const attributes = singularFieldDatum(record)?.attributes
  const isCheckbox = dataType === "checkboxes"
  const isBoolean = dataType === "boolean"
  const isFile = dataType === "file"

  if (isCheckbox) {
    const values = record.relationships.field_data?.data.map(
      (data) => data.attributes.value,
    )

    return values?.length ? values.join(", ") : NO_CUSTOM_FIELD_DATA_VALUE
  }

  if (isFile) {
    const name = attributes?.file_name
    return attributes?.file?.url ? (
      <Link to={attributes.file.url} target="_blank" rel="noopener noreferrer">
        {name}
      </Link>
    ) : (
      (name ?? NO_CUSTOM_FIELD_DATA_VALUE)
    )
  }

  if (isBoolean) {
    if (attributes?.value === "true") return "Yes"
    if (attributes?.value === "false") return "No"

    return NO_CUSTOM_FIELD_DATA_VALUE
  }

  return attributes?.value || NO_CUSTOM_FIELD_DATA_VALUE
}
